import { supabase } from '@/integrations/supabase/client';
import { logger as baseLogger } from '@/lib/structured-logger';
import { ServiceError } from '@/lib/service-error';

const logger = baseLogger.child({ service: 'crm' });

export interface CRMIntegration {
  id: string;
  organization_id: string;
  provider: 'salesforce' | 'hubspot';
  account_name: string | null;
  account_id: string | null;
  is_active: boolean;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CRMDeal {
  id: string;
  crm_integration_id: string;
  organization_id: string;
  deal_id: string;
  deal_name: string;
  deal_stage: string | null;
  deal_amount: number | null;
  close_date: string | null;
  owner_name: string | null;
  owner_email: string | null;
  contact_email: string | null;
  synced_at: string;
}

class CRMService {
  async getIntegrations(organizationId: string): Promise<CRMIntegration[]> {
    try {
      const { data, error } = await supabase
        .from('crm_integrations')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as CRMIntegration[];
    } catch (error) {
      logger.error('Failed to fetch CRM integrations', { error, organizationId });
      throw new ServiceError('Failed to fetch CRM integrations', 'DATABASE_ERROR');
    }
  }

  async saveCredentials(provider: 'salesforce' | 'hubspot', organizationId: string, clientId: string, clientSecret: string): Promise<void> {
    // Note: OAuth credentials (clientId/clientSecret) are app configuration, not user tokens
    // These are saved for OAuth flow initiation - actual tokens are encrypted via oauth-callback
    try {
      const { error } = await supabase
        .from('crm_integrations')
        .upsert({
          organization_id: organizationId,
          provider,
          account_id: clientId, // Store client ID in account_id for reference
          // Do NOT store client secret in database - it should be in environment secrets
          is_active: false, // Not connected yet, just credentials saved
        }, {
          onConflict: 'organization_id,provider'
        });

      if (error) throw error;
      logger.info('CRM credentials saved', { provider, organizationId });
    } catch (error) {
      logger.error('Failed to save CRM credentials', { error, provider });
      throw new ServiceError('Failed to save credentials', 'DATABASE_ERROR');
    }
  }

  async initiateOAuth(provider: 'salesforce' | 'hubspot', organizationId: string, userId: string): Promise<string> {
    try {
      const { data, error } = await supabase.functions.invoke('crm-oauth-start', {
        body: { provider, organizationId, userId }
      });

      if (error) throw error;
      return data.authUrl;
    } catch (error) {
      logger.error('Failed to initiate CRM OAuth', { error, provider });
      throw new ServiceError('Failed to connect CRM', 'NETWORK_ERROR');
    }
  }

  async disconnect(integrationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('crm_integrations')
        .update({ is_active: false })
        .eq('id', integrationId);

      if (error) throw error;
      logger.info('CRM integration disconnected', { integrationId });
    } catch (error) {
      logger.error('Failed to disconnect CRM', { error, integrationId });
      throw new ServiceError('Failed to disconnect CRM', 'DATABASE_ERROR');
    }
  }

  async syncDeals(organizationId: string): Promise<{ totalSynced: number }> {
    try {
      const { data, error } = await supabase.functions.invoke('crm-sync', {
        body: { organizationId }
      });

      if (error) throw error;
      logger.info('CRM deals synced', { organizationId, totalSynced: data.totalSynced });
      return data;
    } catch (error) {
      logger.error('Failed to sync CRM deals', { error, organizationId });
      throw new ServiceError('Failed to sync deals', 'NETWORK_ERROR');
    }
  }

  async getDeals(organizationId: string): Promise<CRMDeal[]> {
    try {
      const { data, error } = await supabase
        .from('crm_deals')
        .select('*')
        .eq('organization_id', organizationId)
        .order('synced_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Failed to fetch CRM deals', { error, organizationId });
      throw new ServiceError('Failed to fetch deals', 'DATABASE_ERROR');
    }
  }
}

export const crmService = new CRMService();
